import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

export const exportExcel = async (rows, filename) => {
  if (!rows.length) return;
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Export');

  const keys = Object.keys(rows[0]);

  // En-têtes
  ws.columns = keys.map(k => ({ header: k, key: k, width: Math.max(k.length + 2, 14) }));

  // Style première ligne : fond vert, texte blanc gras
  const headerRow = ws.getRow(1);
  headerRow.eachCell(cell => {
    cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2D7A2D' } };
    cell.font      = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.alignment = { vertical: 'middle' };
  });
  headerRow.height = 18;

  // Données
  rows.forEach(r => ws.addRow(keys.map(k => r[k] ?? '')));

  // AutoFilter sur toute la plage
  ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: keys.length } };

  const buf = await wb.xlsx.writeBuffer();
  saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${filename}.xlsx`);
};

export const svgToPngBlob = (svgEl, scale = 2) => new Promise(resolve => {
  const { width, height } = svgEl.getBoundingClientRect();
  const w = Math.max(width || 400, 10), h = Math.max(height || 200, 10);
  const clone = svgEl.cloneNode(true);
  clone.setAttribute('width', w); clone.setAttribute('height', h);
  const xml = new XMLSerializer().serializeToString(clone);
  const blob = new Blob([xml], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = w * scale; canvas.height = h * scale;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.scale(scale, scale); ctx.drawImage(img, 0, 0);
    URL.revokeObjectURL(url);
    canvas.toBlob(b => resolve(b), 'image/png');
  };
  img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
  img.src = url;
});

export const downloadSvgAsPng = async (svgEl, filename = 'graphique') => {
  const blob = await svgToPngBlob(svgEl);
  if (!blob) return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = `${filename}.png`; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const copySvgAsPng = async (svgEl) => {
  const blob = await svgToPngBlob(svgEl);
  if (!blob) return;
  try { await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]); }
  catch (e) { console.warn('Copy PNG failed:', e); }
};

export const copyHtmlToClipboard = async (html, plain = '') => {
  try {
    await navigator.clipboard.write([new ClipboardItem({
      'text/html':  new Blob([html],  { type: 'text/html' }),
      'text/plain': new Blob([plain], { type: 'text/plain' }),
    })]);
  } catch { try { await navigator.clipboard.writeText(plain); } catch {} }
};

const SCHEMES = {
  green:  { hdrBg: '#e8f5e9', hdrColor: '#1b5e20', totalBg: '#c8e6c9', totalColor: '#1b5e20', rowAlt: '#f6fdf6' },
  blue:   { hdrBg: '#e3f2fd', hdrColor: '#1565c0', totalBg: '#bbdefb', totalColor: '#0d47a1', rowAlt: '#f0f7ff' },
  brown:  { hdrBg: '#fdf8f4', hdrColor: '#6d4e36', totalBg: '#f5ede4', totalColor: '#5c3317', rowAlt: '#fdf6f0' },
  red:    { hdrBg: '#ffebee', hdrColor: '#c62828', totalBg: '#ffcdd2', totalColor: '#b71c1c', rowAlt: '#fff5f5' },
  purple: { hdrBg: '#f3e5f5', hdrColor: '#6a1b9a', totalBg: '#e1bee7', totalColor: '#4a148c', rowAlt: '#faf4fc' },
  amber:  { hdrBg: '#fff3e0', hdrColor: '#e65100', totalBg: '#ffe0b2', totalColor: '#bf360c', rowAlt: '#fffaf4' },
};

const looksNumeric = v =>
  v != null && v !== '' && v !== '—' && /^-?[\d\s.,]+([MK%])?$/.test(String(v).trim());

export const rowsToHtml = (rows, opts = {}) => {
  if (!rows.length) return { html: '', plain: '' };
  const scheme = typeof opts === 'string' ? opts : (opts.scheme || 'green');
  const { hdrBg, hdrColor, totalBg, totalColor, rowAlt } = SCHEMES[scheme] || SCHEMES.green;
  const keys = Object.keys(rows[0]);

  const lastRow = rows[rows.length - 1];
  const hasTotalRow = lastRow && String(Object.values(lastRow)[0]).toUpperCase() === 'TOTAL';
  const dataRows = hasTotalRow ? rows.slice(0, -1) : rows;

  const firstRow = dataRows[0] || {};
  const rightCols = new Set(keys.filter(k => looksNumeric(firstRow[k])));
  const align = k => rightCols.has(k) ? 'right' : 'left';

  const ths = keys.map(k =>
    `<th style="background:${hdrBg};color:${hdrColor};padding:6px 12px;border:1px solid ${hdrColor}33;font-size:12px;white-space:nowrap;font-weight:700;text-align:${align(k)}">${k}</th>`
  ).join('');

  const trs = dataRows.map((r, i) => {
    const bg = i % 2 === 0 ? '#fff' : rowAlt;
    const tds = keys.map(k =>
      `<td style="background:${bg};padding:5px 12px;border:1px solid #eee;font-size:11px;text-align:${align(k)}">${r[k] ?? ''}</td>`
    ).join('');
    return `<tr>${tds}</tr>`;
  }).join('');

  let totalTr = '';
  if (hasTotalRow) {
    const tds = keys.map((k, i) => {
      const v = lastRow[k] ?? '';
      return `<td style="background:${totalBg};padding:5px 12px;border:1px solid ${totalColor}33;font-size:11px;font-weight:700;color:${totalColor};text-align:${i === 0 ? 'left' : align(k)}">${v}</td>`;
    }).join('');
    totalTr = `<tr>${tds}</tr>`;
  }

  const html = `<table style="border-collapse:collapse;font-family:Arial,sans-serif"><thead><tr>${ths}</tr></thead><tbody>${trs}${totalTr}</tbody></table>`;
  const plain = [keys.join('\t'), ...rows.map(r => keys.map(k => r[k] ?? '').join('\t'))].join('\n');
  return { html, plain };
};

/* ── Export Performance par Commercial ──
   Feuilles : Synthèse générale (general = tous projets cumulés, par commercial),
   Synthèse par ville / par projet (villes / projets = [{ titre, rows }], un bloc titré par groupe),
   Détails (details = lignes ville × projet × commercial × mois, Autres inclus, sans TOTAL).
   En-têtes fusionnés, montants numériques, % au format Excel coloré. */
export const exportObjectifCommercialExcel = async ({ general, villes, projets, details, inds, moisLabel, cumulLabel, filtres, filename }) => {
  if (!general.length) return;
  const wb = new ExcelJS.Workbook();
  const argb  = (hex) => 'FF' + hex.replace('#', '').toUpperCase();
  const fill  = (hex) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb: argb(hex) } });
  const thin  = { style: 'thin',   color: { argb: 'FFD4C8BC' } };
  const thick = { style: 'medium', color: { argb: 'FF8B7B6B' } };
  const NUM   = '#,##0';
  const ECART = '[Color10]+#,##0;[Red]-#,##0;"—"';
  const pctVal  = (c) => (c.o > 0 ? Math.ceil(c.r * 100 / c.o) / 100 : null);
  const ecart   = (c) => (c.o > 0 ? c.r - c.o : 0);
  const pctFill = (p) => (p >= 1 ? '#E8F5E9' : p >= 0.7 ? '#FFF3E0' : '#FFEBEE');
  const pctFont = (p) => (p == null ? '#BBBBBB' : p >= 1 ? '#1B5E20' : p >= 0.7 ? '#E65100' : '#C62828');
  const ROW_BG  = { total: '#F5EDE4' };

  const header = (cell, text, bg, { leftThick = false, size = 10 } = {}) => {
    cell.value = text;
    cell.fill = fill(bg);
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.border = { top: thin, bottom: thin, left: leftThick ? thick : thin, right: thin };
  };
  const titleBlock = (ws, title, lastCol) => {
    ws.mergeCells(1, 1, 1, lastCol);
    ws.getCell(1, 1).value = title;
    ws.getCell(1, 1).font = { bold: true, size: 13, color: { argb: 'FF4A505C' } };
    ws.mergeCells(2, 1, 2, lastCol);
    ws.getCell(2, 1).value = filtres;
    ws.getCell(2, 1).font = { italic: true, size: 9, color: { argb: 'FF888888' } };
  };
  // blocs = [{ c: {r,o}, metrics: ['r','o','e','p'] }]
  const writeRow = (ws, rowIdx, label, kind, blocs) => {
    const row = ws.getRow(rowIdx);
    const bg = ROW_BG[kind];
    const base = { bold: kind === 'total', italic: kind === 'autres' };
    const first = row.getCell(1);
    first.value = label;
    first.font = { ...base, color: { argb: kind === 'autres' ? 'FF666666' : 'FF333333' } };
    first.border = { bottom: thin };
    if (bg) first.fill = fill(bg);
    let col = 2;
    for (const b of blocs) {
      const start = col;
      col += b.metrics.length;
      b.metrics.forEach((m, i) => {
        const cell = row.getCell(start + i);
        cell.border = { bottom: thin, left: i === 0 ? thick : undefined };
        cell.font = { ...base };
        if (bg) cell.fill = fill(bg);
        if (m === 'r') { cell.value = Math.round(b.c.r); cell.numFmt = NUM; }
        if (m === 'o') { cell.value = b.c.o > 0 ? Math.round(b.c.o) : null; cell.numFmt = NUM; cell.font = { ...base, color: { argb: 'FF777777' } }; }
        if (m === 'e') { const e = ecart(b.c); cell.value = e ? Math.round(e) : null; cell.numFmt = ECART; }
        if (m === 'p') {
          const p = pctVal(b.c);
          cell.value = p;
          cell.numFmt = '0%';
          cell.alignment = { horizontal: 'center' };
          cell.font = { bold: true, color: { argb: argb(pctFont(p)) } };
          if (p != null) cell.fill = fill(pctFill(p));
        }
      });
    }
  };

  const groupTitle = (ws, rowIdx, titre, lastCol) => {
    ws.mergeCells(rowIdx, 1, rowIdx, lastCol);
    const cell = ws.getCell(rowIdx, 1);
    cell.value = titre;
    cell.fill = fill('#E0F2F1');
    cell.font = { bold: true, size: 11, color: { argb: 'FF00695C' } };
    cell.border = { top: thick, bottom: thin };
    ws.getRow(rowIdx).height = 18;
  };

  /* ── Feuille synthèse : indicateur > période > Réalisé / Objectif / % ── */
  const syntheseSheet = (name, title, sheetGroups) => {
    const ws = wb.addWorksheet(name, { views: [{ state: 'frozen', xSplit: 1, ySplit: 6 }] });
    const lastCol = 1 + inds.length * 6;
    titleBlock(ws, title, lastCol);
    ws.mergeCells(4, 1, 6, 1); header(ws.getCell(4, 1), 'Commercial', '#4A505C');
    inds.forEach((ind, k) => {
      const c0 = 2 + k * 6;
      ws.mergeCells(4, c0, 4, c0 + 5);     header(ws.getCell(4, c0), ind.label, ind.color, { leftThick: true, size: 11 });
      ws.mergeCells(5, c0, 5, c0 + 2);     header(ws.getCell(5, c0), moisLabel, '#8B5E3C', { leftThick: true });
      ws.mergeCells(5, c0 + 3, 5, c0 + 5); header(ws.getCell(5, c0 + 3), cumulLabel, '#6D4E36', { leftThick: true });
      ['Réalisé', 'Objectif', '%', 'Réalisé', 'Objectif', '%'].forEach((t, i) =>
        header(ws.getCell(6, c0 + i), t, '#A1887F', { leftThick: i === 0 || i === 3 }));
    });
    let r = 7;
    for (const g of sheetGroups) {
      if (g.titre) groupTitle(ws, r++, g.titre, lastCol);
      for (const row of g.rows) {
        writeRow(ws, r++, row.commercial, row.kind, inds.flatMap(i => [
          { c: row[i.key].m, metrics: ['r', 'o', 'p'] },
          { c: row[i.key].c, metrics: ['r', 'o', 'p'] },
        ]));
      }
    }
    ws.getColumn(1).width = 28;
    for (let c = 2; c <= lastCol; c++) ws.getColumn(c).width = (c - 2) % 3 === 2 ? 8 : 13;
    ws.getRow(4).height = 20;
    ws.getRow(5).height = 30;
  };
  syntheseSheet('Synthèse générale', 'Performance par commercial — Synthèse générale (tous projets cumulés)',
    [{ titre: null, rows: general }]);
  syntheseSheet('Synthèse par ville', 'Performance par commercial — Synthèse par ville', villes);
  syntheseSheet('Synthèse par projet', 'Performance par commercial — Synthèse par projet', projets);

  /* ── Feuille Détails : tableau plat ville / projet / commercial / mois (filtrable, tableau croisé) ── */
  {
    const FIXED = ['Ville', 'Projet', 'Commercial', 'Mois'];
    const lastCol = FIXED.length + inds.length * 3;
    const ws = wb.addWorksheet('Détails', { views: [{ state: 'frozen', xSplit: FIXED.length, ySplit: 4 }] });
    titleBlock(ws, 'Performance par commercial — Détails mensuels (janvier → mois sélectionné)', lastCol);
    FIXED.forEach((t, i) => header(ws.getCell(4, 1 + i), t, '#4A505C'));
    inds.forEach((ind, k) => ['Réalisé', 'Objectif', '%'].forEach((t, i) =>
      header(ws.getCell(4, FIXED.length + 1 + k * 3 + i), `${ind.label} ${t}`, ind.color, { leftThick: i === 0 })));
    ws.getRow(4).height = 30;
    let r = 5;
    for (const d of details) {
      const row = ws.getRow(r++);
      const isAutres = d.comId === 'autres';
      const font = { italic: isAutres, color: { argb: isAutres ? 'FF666666' : 'FF333333' } };
      [d.ville, d.projet, d.commercial].forEach((v, i) => { const c = row.getCell(1 + i); c.value = v; c.font = font; c.border = { bottom: thin }; });
      const mc = row.getCell(4);
      mc.value = new Date(Date.UTC(d.annee, d.mois - 1, 1));
      mc.numFmt = 'mmm yyyy';
      mc.alignment = { horizontal: 'left' };
      mc.border = { bottom: thin };
      inds.forEach((ind, k) => {
        const v = d[ind.key];
        const c0 = FIXED.length + 1 + k * 3;
        const cr = row.getCell(c0);
        cr.value = Math.round(v.r); cr.numFmt = NUM; cr.font = font; cr.border = { bottom: thin, left: thick };
        const co = row.getCell(c0 + 1);
        co.value = v.o > 0 ? Math.round(v.o) : null; co.numFmt = NUM; co.font = { ...font, color: { argb: 'FF777777' } }; co.border = { bottom: thin };
        const cp = row.getCell(c0 + 2);
        const p = pctVal(v);
        cp.value = p; cp.numFmt = '0%'; cp.alignment = { horizontal: 'center' }; cp.border = { bottom: thin };
        cp.font = { bold: true, color: { argb: argb(pctFont(p)) } };
        if (p != null) cp.fill = fill(pctFill(p));
      });
    }
    ws.autoFilter = { from: { row: 4, column: 1 }, to: { row: 4, column: lastCol } };
    [16, 18, 26, 11].forEach((w, i) => { ws.getColumn(1 + i).width = w; });
    for (let c = FIXED.length + 1; c <= lastCol; c++) ws.getColumn(c).width = (c - FIXED.length - 1) % 3 === 2 ? 8 : 14;
  }

  const buf = await wb.xlsx.writeBuffer();
  saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${filename}.xlsx`);
};
